import { useEffect, useState } from "react";
import { api, getUser } from "@/lib/api";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";

const VERTS = [
  { id: "transport", name: "Transport", color: "#1E3A8A" },
  { id: "delivery", name: "Delivery", color: "#065F46" },
  { id: "home", name: "Home", color: "#9A3412" },
  { id: "concierge", name: "Concierge", color: "#4C1D95" },
  { id: "business", name: "Business", color: "#334155" },
];

export default function BecomeProvider() {
  const nav = useNavigate();
  const user = getUser();
  const [existing, setExisting] = useState(null);
  const [selected, setSelected] = useState([]);
  const [years, setYears] = useState(1);
  const [bio, setBio] = useState("");
  const [serviceArea, setServiceArea] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) api.get("/providers/me").then(res => setExisting(res.data)).catch(() => {});
  }, []);

  const toggle = (v) => {
    setSelected(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please sign up as a provider first");
      nav("/signup");
      return;
    }
    if (selected.length === 0) {
      toast.error("Select at least one vertical");
      return;
    }
    setLoading(true);
    try {
      await api.post("/providers/apply", {
        verticals: selected,
        experience_years: parseInt(years),
        bio,
        service_area: serviceArea,
      });
      toast.success("Application submitted");
      nav("/provider-dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Application failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16" data-testid="become-provider-page">
      <div className="eyebrow mb-4">Independent Providers</div>
      <h1 className="font-serif-display text-4xl lg:text-6xl text-white leading-tight">
        Own your schedule.<br />Keep <span className="gold-text italic">80%</span> of every job.
      </h1>
      <p className="mt-8 text-lg text-zinc-400 max-w-2xl">
        Apply to become an independent provider on RideXelite Services. Choose any combination
        of verticals from transport to home services, and the platform will route paid,
        scheduled jobs to you.
      </p>

      {!user && (
        <div className="mt-10 rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-6">
          <p className="text-white">You need an account to apply.</p>
          <div className="mt-4 flex gap-3">
            <Link to="/signup" className="px-6 py-3 rounded-full bg-[#D4AF37] text-black text-sm font-semibold">Sign up</Link>
            <Link to="/login" className="px-6 py-3 rounded-full border border-white/20 text-sm">Sign in</Link>
          </div>
        </div>
      )}

      {user && existing && (
        <div className="mt-10 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 flex items-start gap-4">
          <CheckCircle size={28} weight="fill" className="text-emerald-400 mt-1" />
          <div>
            <div className="text-white font-medium">Application submitted</div>
            <div className="text-sm text-zinc-400 mt-1">Status: {existing.status}. Verticals: {existing.verticals.join(", ")}.</div>
            <Link to="/provider-dashboard" className="mt-3 inline-flex items-center gap-2 text-[#D4AF37]">Go to provider dashboard <ArrowRight size={14} /></Link>
          </div>
        </div>
      )}

      {user && !existing && (
        <form onSubmit={submit} className="mt-12 space-y-8">
          <div>
            <label className="eyebrow block mb-4">Select verticals you offer</label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {VERTS.map(v => {
                const on = selected.includes(v.id);
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => toggle(v.id)}
                    data-testid={`vert-toggle-${v.id}`}
                    className={`p-4 rounded-2xl border text-left transition ${on ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/30"}`}
                    style={on ? { borderColor: v.color, background: `${v.color}20` } : {}}
                  >
                    <div className="w-2 h-8 rounded-full mb-2" style={{ background: v.color }} />
                    <div className="text-white font-medium text-sm">{v.name}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="eyebrow block mb-3">Years of experience</label>
              <input
                type="number"
                min={0}
                value={years}
                onChange={e => setYears(e.target.value)}
                data-testid="provider-years-input"
                className="w-full px-4 py-4 rounded-xl bg-[#121214] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="eyebrow block mb-3">Service area</label>
              <input
                type="text"
                value={serviceArea}
                onChange={e => setServiceArea(e.target.value)}
                required
                placeholder="e.g., Greater NYC"
                data-testid="provider-area-input"
                className="w-full px-4 py-4 rounded-xl bg-[#121214] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-3">Short bio</label>
            <textarea
              value={bio}
              onChange={e => setBio(e.target.value)}
              rows={4}
              required
              placeholder="Tell us about your experience..."
              data-testid="provider-bio-input"
              className="w-full px-4 py-4 rounded-xl bg-[#121214] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#121214] p-6 flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="eyebrow gold-text">Commission</div>
              <div className="font-serif-display text-2xl text-white">Platform takes 20% · You keep 80%</div>
            </div>
            <div className="text-sm text-zinc-400 max-w-md">
              Applied to every completed job. Payouts processed on completion.
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="provider-apply-btn"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#D4AF37] text-black font-semibold disabled:opacity-60"
          >
            {loading ? "Submitting…" : "Submit application"} <ArrowRight size={16} weight="bold" />
          </button>
        </form>
      )}
    </div>
  );
}
